if (!customElements.get('main-search')) {
  const MainSearch = class extends HTMLElement {
    constructor() {
      super();

      this.querySelectorAll('.main-search__close').forEach((el) => {
        el.addEventListener('click', (evt) => {
          evt.preventDefault();
          document.body.classList.remove('show-search');
        });
      });

      const searchInput = this.querySelector('.main-search__input');
      if (!searchInput) {
        return;
      }

      searchInput.addEventListener('keyup', (evt) => {
        if (evt.key === 'Escape') {
          const closeButton = this.querySelector('.main-search__close');
          if (closeButton) {
            closeButton.dispatchEvent(new Event('click'));
          } else {
            document.body.classList.remove('show-search');
          }
        }
      });

      if (this.dataset.cardStyle === 'minimal') {
        this.classList.add('main-search--card-minimal');
      }

      if (this.dataset.quickSearch === 'true') {
        this.initQuickSearch();
      }
    }

    /**
     * Extracts suggestion links from <search-suggestions> and builds
     * a wrap-based chip bar with "ver mais" toggle when overflowing.
     */
    buildSuggestionsBar() {
      const sugEl = this.querySelector('search-suggestions');
      if (!sugEl) return null;

      const links = sugEl.querySelectorAll('a');
      if (!links.length) return null;

      const bar = document.createElement('div');
      bar.className = 'mini-search-suggestions-bar';

      const header = document.createElement('div');
      header.className = 'mini-search-suggestions-header';

      const label = document.createElement('span');
      label.className = 'mini-search-suggestions-label';
      label.textContent = 'Sugestões:';
      header.appendChild(label);

      const toggle = document.createElement('button');
      toggle.className = 'mini-search-suggestions-toggle';
      toggle.type = 'button';
      toggle.textContent = 'ver mais';
      toggle.style.display = 'none';
      header.appendChild(toggle);

      bar.appendChild(header);

      const track = document.createElement('div');
      track.className = 'mini-search-suggestions-track';

      links.forEach((link) => {
        const chip = document.createElement('a');
        chip.className = 'mini-search-suggestions-chip';
        chip.href = link.href;
        chip.textContent = link.textContent.trim();
        track.appendChild(chip);
      });

      bar.appendChild(track);

      let expanded = false;
      toggle.addEventListener('click', () => {
        expanded = !expanded;
        track.classList.toggle('mini-search-suggestions-track--expanded', expanded);
        toggle.textContent = expanded ? 'ver menos' : 'ver mais';
      });

      // Show toggle only if content overflows the collapsed max-height
      requestAnimationFrame(() => {
        if (track.scrollHeight > track.clientHeight + 2) {
          toggle.style.display = '';
        }
      });

      return bar;
    }

    /**
     * Builds a compact mini card from a product-block element.
     */
    buildMinimalCard(block) {
      const linkEl   = block.querySelector('a[href]');
      const href     = linkEl ? linkEl.href : '#';
      const imgEl    = block.querySelector('img');
      const imgSrc   = imgEl ? (imgEl.dataset.src || imgEl.currentSrc || imgEl.src) : '';
      const imgAlt   = imgEl ? (imgEl.alt || '') : '';

      const titleEl  = block.querySelector(
        '.product-block__title, .product__title, [class*="product-title"], h2, h3, h4'
      );
      let title = titleEl
        ? titleEl.innerText.trim()
        : (linkEl ? linkEl.innerText.replace(/\s+/g, ' ').trim() : '');

      const priceEl  = block.querySelector('.price, [class*="product-price"], [class*="price"]');
      let priceHTML = '';

      if (priceEl) {
        const raw = priceEl.innerText.replace(/\s+/g, ' ').trim();
        const allPrices = raw.match(/R\$\s?[\d.,]+/g) || [];
        const hasPromo = /preço promocional/i.test(raw);

        if (hasPromo && allPrices.length >= 2) {
          const salePrice    = allPrices[0].trim();
          const comparePrice = allPrices[1].trim();
          priceHTML = `<span class="mini-search-card__price">
            <span class="mini-search-card__price-compare">${comparePrice}</span>
            <span class="mini-search-card__price-sale">${salePrice}</span>
          </span>`;
        } else if (allPrices.length === 1) {
          priceHTML = `<span class="mini-search-card__price">${allPrices[0].trim()}</span>`;
        } else if (allPrices.length >= 2) {
          const salePrice    = allPrices[0].trim();
          const comparePrice = allPrices[1].trim();
          priceHTML = `<span class="mini-search-card__price">
            <span class="mini-search-card__price-compare">${comparePrice}</span>
            <span class="mini-search-card__price-sale">${salePrice}</span>
          </span>`;
        }
      }

      const card = document.createElement('a');
      card.className   = 'mini-search-card';
      card.href        = href;
      card.setAttribute('aria-label', title);
      card.innerHTML   = `
        <div class="mini-search-card__img" aria-hidden="true">
          ${imgSrc
            ? `<img src="${imgSrc}" alt="${imgAlt.replace(/"/g, '&quot;')}" loading="lazy" width="56" height="56">`
            : '<span class="mini-search-card__img-placeholder"></span>'
          }
        </div>
        <div class="mini-search-card__info">
          <span class="mini-search-card__title">${title}</span>
          ${priceHTML}
        </div>
      `;
      return card;
    }

    initQuickSearch() {
      const searchInput = this.querySelector('.main-search__input');
      const resultsBox  = this.querySelector('.main-search__results');
      if (!searchInput || !resultsBox) {
        return;
      }
      
      // ── ARIA (ported from native main-search.js) ───────────────────────────
      // Applied via JS so the custom search markup stays untouched. IDs are
      // per-instance to avoid collisions when several <main-search> render at
      // once (inline + overlay).
      MainSearch.ariaCount = (MainSearch.ariaCount || 0) + 1;
      const ariaId = `MainSearch${MainSearch.ariaCount}`;
      if (!resultsBox.id) resultsBox.id = `${ariaId}Results`;
      resultsBox.setAttribute('role', 'listbox');
      searchInput.setAttribute('aria-autocomplete', 'list');
      searchInput.setAttribute('aria-controls', resultsBox.id);
      searchInput.setAttribute('aria-expanded', 'false');

      // Per-instance screen-reader status region (announces result counts).
      let statusRegion = this.querySelector('.main-search__status');
      if (!statusRegion) {
        statusRegion = document.createElement('div');
        statusRegion.className = 'main-search__status visually-hidden';
        statusRegion.setAttribute('role', 'status');
        statusRegion.setAttribute('aria-live', 'polite');
        this.appendChild(statusRegion);
      }

      const setExpanded = (open) => searchInput.setAttribute('aria-expanded', open ? 'true' : 'false');

      const announce = (messageEl) => {
        if (statusRegion && messageEl) statusRegion.innerHTML = messageEl.innerHTML;
      };

      // Tag rendered results as listbox options + reset the keyboard cursor.
      const assignOptions = () => {
        this.activeResultIndex = -1;
        searchInput.removeAttribute('aria-activedescendant');
        resultsBox.querySelectorAll('.mini-search-card, .main-search-result').forEach((el, index) => {
          el.setAttribute('role', 'option');
          if (!el.id) el.id = `${ariaId}Option${index}`;
        });
      };

      const clearActiveOption = () => {
        resultsBox.querySelectorAll('[role="option"]').forEach((opt) => opt.classList.remove('search-result-active'));
        searchInput.removeAttribute('aria-activedescendant');
        this.activeResultIndex = -1;
      };
      this._clearActiveOption = clearActiveOption;

      // Keyboard navigation over results (ArrowUp/Down + Enter). When no option
      // is active, Enter is left untouched so the form submits as before.
      this.activeResultIndex = -1;
      searchInput.addEventListener('keydown', (evt) => {
        const options = [...resultsBox.querySelectorAll('[role="option"]')];
        if (!options.length) return;

        const updateActiveOption = () => {
          options.forEach((opt) => opt.classList.remove('search-result-active'));
          const active = options[this.activeResultIndex];
          if (!active) return;
          active.classList.add('search-result-active');
          if (!active.id) active.id = `${ariaId}Option${this.activeResultIndex}`;
          searchInput.setAttribute('aria-activedescendant', active.id);
          active.scrollIntoView({ block: 'nearest' });
        };

        if (evt.key === 'ArrowDown') {
          evt.preventDefault();
          this.activeResultIndex = Math.min(this.activeResultIndex + 1, options.length - 1);
          updateActiveOption();
        } else if (evt.key === 'ArrowUp') {
          evt.preventDefault();
          this.activeResultIndex = Math.max(this.activeResultIndex - 1, 0);
          updateActiveOption();
        } else if (evt.key === 'Enter' && this.activeResultIndex >= 0) {
          const active = options[this.activeResultIndex];
          if (active) {
            evt.preventDefault();
            const link = active.matches('a[href]') ? active : active.querySelector('a[href]');
            if (link) link.click();
          }
        }
      });

      const isMinimal    = this.dataset.cardStyle === 'minimal';
      const DESKTOP_MIN  = 992;

      /**
       * Overlay = the fullscreen search panel (has a close button).
       * Inline  = the search bar embedded in the header.
       * The overlay must ALWAYS use the default full-card rendering,
       * even if data-card-style="minimal" were set on it.
       */
      const isOverlay = !!this.querySelector('.main-search__close');

      /**
       * Use minimal portal (move resultsBox to <body>) only on desktop inline.
       */
      const useMinimalPortal = () => isMinimal && !isOverlay && window.innerWidth >= DESKTOP_MIN;

      /**
       * Use the minimal card visual (mini cards stacked) whenever
       * card-style is minimal — desktop inline uses the portal,
       * overlay/mobile uses the same visual but inside its own resultsBox.
       */
      const useMinimalCards = () => isMinimal;

      /**
       * PORTAL POSITIONING — desktop inline minimal only.
       * Never create a portal for the overlay instance.
       */
      if (isMinimal && !isOverlay) {
        const inputContainer = this.querySelector('.main-search__input-container');

        if (inputContainer) {
          document.body.appendChild(resultsBox);
          resultsBox.classList.add('mini-search-portal');

          const positionPortal = () => {
            if (window.innerWidth < DESKTOP_MIN) {
              resultsBox.style.display = 'none';
              return;
            }
            const rect = inputContainer.getBoundingClientRect();
            resultsBox.style.position = 'fixed';
            resultsBox.style.top      = rect.bottom + 'px';
            resultsBox.style.left     = rect.left   + 'px';
            resultsBox.style.width    = rect.width  + 'px';
            resultsBox.style.display  = '';
          };

          window.addEventListener('scroll',  positionPortal, { passive: true });
          window.addEventListener('resize',  positionPortal, { passive: true });
          this._positionPortal = positionPortal;
        }
      }

      const searchTimeoutThrottle = 500;
      const resultLimit           = isMinimal ? 12 : 8;
      const includeMeta           = this.dataset.quickSearchMeta === 'true';
      let searchTimeoutID         = -1;
      let searchAbortController   = null;

      /** Clear all search UI states and empty results. */
      const clearSearchState = () => {
        this.oldSearchValue = '';
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);
        this.classList.remove(
          'main-search--has-results',
          'main-search--results-on-multiple-lines',
          'main-search--no-results',
          'main-search--loading'
        );
        resultsBox.innerHTML = '';
        setExpanded(false);
        if (this._clearActiveOption) this._clearActiveOption();
        if (this._positionPortal) this._positionPortal();
      };

      const handleInputChange = () => {
        const valueToSearch = searchInput.value;

        if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
          this.oldSearchValue = valueToSearch;

          if (searchAbortController !== null) {
            searchAbortController.abort('Existing request not needed');
            searchAbortController = null;
          }

          clearTimeout(searchTimeoutID);

          const form       = searchInput.closest('form');
          const linkURL    = new URL(form.action);
          const formParams = new URLSearchParams(new FormData(form));
          formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

          this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
          this.classList.add('main-search--loading');
          if (!this.querySelector('.main-search__results-spinner')) {
            resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
          }

          searchTimeoutID = setTimeout(() => {
            searchAbortController = new AbortController();
            let ajaxUrl;

            if (theme.Shopify.features.predictiveSearch) {
              ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
              ajaxUrl.searchParams.set('q', valueToSearch);
              ajaxUrl.searchParams.set('section_id', 'predictive-search');
              ajaxUrl.searchParams.set('resources[limit]', resultLimit);
              ajaxUrl.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor');
            } else {
              ajaxUrl = new URL(linkURL);
              ajaxUrl.searchParams.set('section_id', 'main-search');
            }

            fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
              })
              .then((response) => {
                const template = document.createElement('template');
                template.innerHTML = response;
                let resultsList = null;
                let resultsMessage = null;
                if (theme.Shopify.features.predictiveSearch) {
                  resultsList = template.content.querySelector('.product-grid');
                  resultsMessage = template.content.querySelector('[data-results-message]');
                } else {
                  resultsList = template.content.querySelector('.section-search-template .product-grid');
                  resultsMessage = template.content.querySelector('.section-search-template [data-results-message]');
                }

                if (!resultsList) {
                  this.classList.remove('main-search--loading');
                  this.classList.add('main-search--no-results');
                  resultsBox.innerHTML = '';
                  setExpanded(false);
                  return;
                }

                searchAbortController = null;
                this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
                resultsBox.innerHTML = '';

                // ── MINIMAL CARD RENDERING (desktop + mobile) ──────────────
                // Uses mini stacked cards visual. On desktop inline the
                // resultsBox lives in a portal (<body>); on mobile/overlay
                // the resultsBox stays in its original DOM position.
                if (useMinimalCards()) {
                  const productBlocks = [
                    ...resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)')
                  ];

                  this.classList.remove('main-search--loading');

                  if (productBlocks.length) {
                    this.classList.add('main-search--has-results');

                    // ── Suggestions chips bar ──
                    const sugBar = this.buildSuggestionsBar();
                    if (sugBar) resultsBox.appendChild(sugBar);

                    const listWrapper = document.createElement('div');
                    listWrapper.className = 'mini-search-results-list';

                    productBlocks.forEach((block) => {
                      listWrapper.appendChild(this.buildMinimalCard(block));
                    });

                    resultsBox.appendChild(listWrapper);

                    // Apply fade mask only when list actually scrolls
                    requestAnimationFrame(() => {
                      if (listWrapper.scrollHeight > listWrapper.clientHeight) {
                        listWrapper.classList.add('mini-search-results-list--has-scroll');
                      }
                    });

                    const allContainer = document.createElement('div');
                    allContainer.className = 'mini-search-view-all-container';
                    const allLink       = document.createElement('a');
                    allLink.className   = 'mini-search-view-all';
                    allLink.href        = linkURL;
                    allLink.textContent = theme.strings.generalSearchViewAll || 'View all results';
                    allContainer.appendChild(allLink);
                    resultsBox.appendChild(allContainer);

                    assignOptions();
                    setExpanded(true);
                    announce(resultsMessage);
                  } else {
                    this.classList.add('main-search--no-results');
                    const emptyMessage     = document.createElement('div');
                    emptyMessage.className = 'mini-search-empty';
                    emptyMessage.textContent = theme.strings.generalSearchNoResultsWithoutTerms || 'No results found.';
                    resultsBox.appendChild(emptyMessage);
                    setExpanded(false);
                    clearActiveOption();
                  }

                  if (this._positionPortal) this._positionPortal();
                  return;
                }

                // ── DEFAULT (full card) RENDERING ───────────────────────────
                // Overlay (mobile + desktop), inline on mobile, inline default style
                const resultsProducts = document.createElement('div');
                resultsProducts.className = 'main-search__results__products collection-listing';
                resultsProducts.innerHTML = '<div></div>';
                const resultsPages = document.createElement('div');
                resultsPages.className = 'main-search__results__pages';

                resultsProducts.firstElementChild.className = resultsList.className;

                resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                  if (index <= resultLimit) {
                    block.classList.add('main-search-result');
                    block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                    block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                    resultsProducts.firstElementChild.appendChild(block);
                  }
                });

                resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                  const item = document.createElement('a');
                  item.className = 'main-search-result main-search-result--page';
                  item.href = block.querySelector('a').href;
                  item.innerHTML = '<div class="main-search-result__text"></div>';
                  item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                  resultsPages.appendChild(item);
                });

                this.classList.remove('main-search--loading');

                const areProducts = !!resultsProducts.querySelector('.main-search-result');
                const arePages    = !!resultsPages.querySelector('.main-search-result');
                if (areProducts || arePages) {
                  this.classList.add('main-search--has-results');
                  this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);
                  if (areProducts) {
                    resultsBox.appendChild(resultsProducts);
                  }
                  if (arePages) {
                    const heading       = document.createElement('h6');
                    heading.className   = 'main-search-result__heading';
                    heading.innerHTML   = theme.strings.generalSearchPages;
                    resultsPages.insertAdjacentElement('afterbegin', heading);
                    resultsBox.appendChild(resultsPages);
                  }

                  const allLink       = document.createElement('a');
                  allLink.className   = 'main-search__results-all-link btn btn--secondary';
                  allLink.href        = linkURL;
                  allLink.innerHTML   = theme.strings.generalSearchViewAll;
                  resultsBox.appendChild(allLink);
                  
                  assignOptions();
                  setExpanded(true);
                  announce(resultsMessage);
                } else {
                  this.classList.add('main-search--no-results');
                  const emptyMessage     = document.createElement('div');
                  emptyMessage.className = 'main-search__empty-message';
                  emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                  resultsBox.appendChild(emptyMessage);
                  setExpanded(false);
                  clearActiveOption();
                }
              })
              .catch((error) => {
                console.warn(error); // eslint-disable-line no-console
              });
          }, searchTimeoutThrottle);
        } else if (!valueToSearch.length) {
          clearSearchState();
        }
      };

      searchInput.addEventListener('keyup', handleInputChange.bind(this));
      searchInput.addEventListener('change', handleInputChange.bind(this));

      // ── Close on input clear (backspace/delete/select-all+delete) ──
      searchInput.addEventListener('input', () => {
        if (!searchInput.value.length) {
          clearSearchState();
        }
      });

      // ── Close on click outside (input + portal/results) ──
      document.addEventListener('click', (evt) => {
        if (this.contains(evt.target)) return;
        if (resultsBox.contains(evt.target)) return;
        clearSearchState();
      });
    }
  };

  window.customElements.define('main-search', MainSearch);
}
